import type { AuthUser } from '@offer/auth-lib'
import * as amplitude from '@amplitude/analytics-browser'
import { environment } from '@/environments'

const API_KEY = environment.env === 'production' ? environment.amplitudeApiKey : environment.amplitudeApiKeyDev
type AnalyticsIdentity = Pick<AuthUser, 'id' | 'name' | 'job'>

let analyticsIdentity: AnalyticsIdentity | null = null
const analyticsIdentityListeners = new Set<() => void>()

function notifyAnalyticsIdentityListeners() {
  for (const listener of analyticsIdentityListeners)
    listener()
}

export const ANALYTICS_EVENTS = {
  PAGE_VIEWED: 'page_viewed',
  QUICK_SEARCH_OPENED: 'quick_search_opened',
  QUICK_SEARCH_QUERY_SUBMITTED: 'quick_search_query_submitted',
  QUICK_SEARCH_VIEW_CHANGED: 'quick_search_view_changed',
  QUICK_SEARCH_RESULT_CLICKED: 'quick_search_result_clicked',
  QUICK_SEARCH_SEE_ALL_CLICKED: 'quick_search_see_all_clicked',
  NAV_UNIVERSE_SELECTED: 'nav_universe_selected',
  NAV_DEPARTMENT_SELECTED: 'nav_department_selected',
  NAV_SUBDEPARTMENT_TOGGLED: 'nav_subdepartment_toggled',
  NAV_SUBDEPARTMENT_FAMILIES_LOADED: 'nav_subdepartment_families_loaded',
  NAV_FAMILY_TOGGLED: 'nav_family_toggled',
  NAV_FAMILIES_SELECT_ALL_TOGGLED: 'nav_families_select_all_toggled',
  NAV_FAMILIES_SUBMITTED: 'nav_families_submitted',
  PRODUCT_FILTERS_PANEL_TOGGLED: 'product_filters_panel_toggled',
  PRODUCT_FILTER_TOGGLED: 'product_filter_toggled',
  PRODUCT_FILTER_USED: 'product_filter_used',
  PRODUCT_FILTERS_RESET: 'product_filters_reset',
  PRODUCT_NEW_PRODUCT_ONLY_TOGGLED: 'product_new_product_only_toggled',
  PRODUCTS_PAGE_OPENED: 'products_page_opened',
  PRODUCTS_SORT_CHANGED: 'products_sort_changed',
  PRODUCTS_VIEW_MODE_SWITCHED: 'products_view_mode_switched',
  PRODUCTS_SELECTED_FAMILIES_MODAL_OPENED: 'products_selected_families_modal_opened',
  PRODUCTS_SELECTED_FAMILIES_REMOVAL_CONFIRMED: 'products_selected_families_removal_confirmed',
  PRODUCT_GALLERY_ZOOM_CHANGED: 'product_gallery_zoom_changed',
  PRODUCT_GALLERY_CUSTOM_VIEW_APPLIED: 'product_gallery_custom_view_applied',
  PRODUCTS_LIST_CUSTOM_VIEW_APPLIED: 'products_list_custom_view_applied',
  NAV_ALL_PRODUCTS_CLICKED: 'nav_all_products_clicked',
  PRODUCTS_TAB_CLICKED: 'products_tab_clicked',
  SEASON_SELECTED: 'season_selected',
  SEASON_TAG_CLICKED: 'season_tag_clicked',
  COUNTRY_SELECTION_VALIDATED: 'country_selection_validated',
  ONBOARDING_TOUR_DISPLAYED: 'onboarding_tour_displayed',
  ONBOARDING_TOUR_COMPLETED: 'onboarding_tour_completed',
  ACCESS_DENIED_PAGE_VIEWED: 'access_denied_page_viewed',
  ACCESS_DENIED_PAGE_EXITED: 'access_denied_page_exited',
  DECATHNOSAURE_GAME_OVER: 'decathnosaure_game_over',
} as const

export const ANALYTICS_JOURNEY_CHANNELS = {
  QUICK_SEARCH: 'quick_search',
  HIERARCHY_NAVIGATION: 'hierarchy_navigation',
} as const

const JOURNEY_CHANNEL_STORAGE_KEY = 'offer_catalog.pending_journey_channel'
const FIRST_NAVIGATION_METHOD_STORAGE_KEY = 'offer_catalog.first_navigation_method'

type AnalyticsPrimitive = string | number | boolean | null
type AnalyticsPropertyValue = AnalyticsPrimitive | readonly AnalyticsPrimitive[]
export type AnalyticsEventProperties = Record<string, AnalyticsPropertyValue | undefined>

export function subscribeToAnalyticsIdentity(listener: () => void) {
  analyticsIdentityListeners.add(listener)
  return () => analyticsIdentityListeners.delete(listener)
}

export function getAnalyticsIdentityUserId() {
  return analyticsIdentity?.id ?? null
}

export function setAnalyticsIdentity(user: AnalyticsIdentity) {
  if (!API_KEY || !user.id)
    return

  if (
    analyticsIdentity?.id === user.id
    && analyticsIdentity.name === user.name
    && analyticsIdentity.job === user.job
  ) {
    return
  }

  amplitude.setUserId(user.id)
  const identityEvent = new amplitude.Identify()
  identityEvent.set('full_name', user.name ?? '')
  identityEvent.set('job', user.job ?? '')
  identityEvent.set('uid', user.id)
  amplitude.identify(identityEvent)
  analyticsIdentity = user
  notifyAnalyticsIdentityListeners()
}

export function clearAnalyticsIdentity() {
  if (!analyticsIdentity)
    return

  amplitude.reset()
  analyticsIdentity = null
  notifyAnalyticsIdentityListeners()
}

export function trackAnalyticsEvent(eventName: string, eventProperties: AnalyticsEventProperties = {}) {
  if (!API_KEY || !analyticsIdentity) {
    return
  }
  amplitude.track(eventName, eventProperties)
}

export function setPendingJourneyChannel(channel: string) {
  return window.sessionStorage.setItem(JOURNEY_CHANNEL_STORAGE_KEY, channel)
}

export function consumePendingJourneyChannel() {
  const channel = window.sessionStorage.getItem(JOURNEY_CHANNEL_STORAGE_KEY)
  if (channel) {
    window.sessionStorage.removeItem(JOURNEY_CHANNEL_STORAGE_KEY)
  }

  return channel
}

export function trackNavigationEvent(
  eventName: string,
  navigationMethod: 'quick_search' | 'hierarchy',
  eventProperties: AnalyticsEventProperties = {},
) {
  if (!API_KEY || !analyticsIdentity)
    return

  const isFirstNavigationAction = !window.sessionStorage.getItem(FIRST_NAVIGATION_METHOD_STORAGE_KEY)

  trackAnalyticsEvent(eventName, {
    ...eventProperties,
    navigation_method: navigationMethod,
    is_first_navigation_action: isFirstNavigationAction,
  })

  if (isFirstNavigationAction) {
    window.sessionStorage.setItem(FIRST_NAVIGATION_METHOD_STORAGE_KEY, navigationMethod)
  }
}

export function initAnalytics() {
  if (!API_KEY) {
    console.warn('Amplitude API Key is missing. Analytics will not be initialized.')
    return
  }

  amplitude.init(API_KEY, {
    serverZone: 'EU',
    defaultTracking: {
      sessions: false,
      pageViews: false,
    },
    autocapture: false,
  })
  window.amplitude = amplitude
};
